import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/components/ui/text/Text';
import { TABLE_PALETTE } from '@/components/ui/tables/Table.constants';
import type { AuditRecord } from '@/api/domains/audit/types';

// ── Types ──────────────────────────────────────────────────────────────────

export interface AuditExpandedRowProps {
  record: AuditRecord;
}

// ── Constants ─────────────────────────────────────────────────────────────

const CREATE_OR_MODIFY_ACTIONS = new Set(['Creación', 'Modificación']);

// ── Helpers ────────────────────────────────────────────────────────────────

function formatChangesJson(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null) return null;
    const formatted = JSON.stringify(parsed, null, 2);
    if (formatted === '[]' || formatted === '{}') return null;
    return formatted;
  } catch {
    return null;
  }
}

// ── Component ──────────────────────────────────────────────────────────────

export const AuditExpandedRow: React.FC<AuditExpandedRowProps> = ({ record }) => {
  const { t: translate } = useTranslation();

  const changesJson = formatChangesJson(record.changes);
  const showChanges = CREATE_OR_MODIFY_ACTIONS.has(record.action) && changesJson !== null;

  return (
    <div className="py-3 grid grid-cols-[auto_1fr] gap-x-8 gap-y-2 items-start" data-testid="audit-expanded-row">

      {/* Detalle */}
      <Text variant="small-medium" color={TABLE_PALETTE.headerText} className="whitespace-nowrap font-bold">
        {translate('audit:expanded.detail')}:
      </Text>
      <Text variant="small-medium" color={TABLE_PALETTE.rowText} className="whitespace-pre-line">
        {record.detail ?? '-'}
      </Text>

      {/* Origen */}
      <Text variant="small-medium" color={TABLE_PALETTE.headerText} className="whitespace-nowrap font-bold">
        {translate('audit:expanded.origin')}:
      </Text>
      <Text variant="small-medium" color={TABLE_PALETTE.rowText}>
        {record.origin ?? '-'}
      </Text>

      {/* Id objeto */}
      <Text variant="small-medium" color={TABLE_PALETTE.headerText} className="whitespace-nowrap font-bold">
        {translate('audit:expanded.objectId')}:
      </Text>
      <Text variant="small-medium" color={TABLE_PALETTE.rowText}>
        {record.objectId ?? '-'}
      </Text>

      {/* Cambios */}
      {showChanges && (
        <>
          <Text variant="small-medium" color={TABLE_PALETTE.headerText} className="whitespace-nowrap font-bold pt-1">
            {translate('audit:expanded.changes')}:
          </Text>
          <div data-testid="audit-changes-box">
            <pre
              className="overflow-y-auto max-h-64 rounded border border-gray-200 bg-gray-50 p-2 text-xs leading-relaxed text-gray-800 whitespace-pre-wrap break-words"
              data-testid="audit-changes-pre"
            >
              {changesJson}
            </pre>
          </div>
        </>
      )}

      {/* IP usuario */}
      <Text variant="small-medium" color={TABLE_PALETTE.headerText} className="whitespace-nowrap font-bold">
        {translate('audit:expanded.ipUser')}:
      </Text>
      <Text variant="small-medium" color={TABLE_PALETTE.rowText}>
        {record.ipUser ?? '-'}
      </Text>

    </div>
  );
};
